import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Workflow } from "lucide-react";

const DEMO = [["Admin", "bookingappointment49@gmail.com", "Saminfra@2026"], ["Manager", "manager@saminfratech.com", "Manager@123"], ["Executive", "rahul@saminfratech.com", "Sales@123"]];

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await login(email, password); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen bg-[#F2F4F1] text-neutral-800 lg:grid-cols-2">
      <div className="relative hidden overflow-hidden rounded-r-[48px] bg-[#1F9E7A] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[#F5E04F]" />
        <div className="absolute -bottom-10 right-40 h-40 w-40 rounded-full bg-[#F7A934]" />
        <div className="relative flex items-center gap-2"><div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#232323] font-display font-bold text-[#F5E04F]">SI</div><span className="font-display text-lg font-bold">Saminfratech</span></div>
        <div className="relative max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F5E04F]">Deterministic Sales Engine</p>
          <h1 className="font-display mt-4 text-4xl font-bold leading-tight text-white lg:text-5xl">Every meeting ends with an outcome.</h1>
          <p className="mt-5 text-white/80">One pipeline for software builds, mobile apps, digital marketing retainers and managed IT — with a meeting state machine that never lets a lead go dark.</p>
        </div>
        <div className="relative flex items-center gap-3 font-mono text-xs text-white/70"><Workflow className="h-4 w-4" />Scheduled → In Progress → Done / No-Show / Rescheduled / Cancelled</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div><h2 className="font-display text-2xl font-bold text-[#151515]">Sign in</h2><p className="mt-1 text-sm text-neutral-500">Access your daily follow-up queue.</p></div>
          <Input data-testid="login-email-input" type="email" required placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 border-neutral-200 bg-white" />
          <Input data-testid="login-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 border-neutral-200 bg-white" />
          {err && <p data-testid="login-error" className="text-sm text-rose-600">{err}</p>}
          <Button data-testid="login-submit-btn" disabled={busy} className="h-12 w-full bg-[#1F9E7A] text-base hover:bg-[#178566]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
          <div className="rounded-2xl border border-neutral-200 p-3">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-neutral-400">Demo accounts</p>
            <div className="flex flex-wrap gap-2">
              {DEMO.map(([r, e, p]) => <button type="button" key={r} data-testid={`demo-login-${r.toLowerCase()}`} onClick={() => { setEmail(e); setPassword(p); }} className="rounded-md border border-neutral-200 px-2.5 py-1 text-xs text-neutral-600 hover:border-[#1F9E7A]">{r}</button>)}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
